"use client"

import * as React from "react"
import { User, Palette, Shield, ChevronRight, Volume2, FileAudio, X, CheckCircle2, KeyRound, Maximize2, Camera, Loader2, ShieldCheck, Clock, Smartphone } from "lucide-react"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Attachment, AttachmentMedia, AttachmentContent, AttachmentTitle, AttachmentDescription, AttachmentActions, AttachmentAction } from "@/components/ui/attachment"
import { SupabaseUpload } from "@/components/SupabaseUpload"
import { UserProfile, Language } from "@/types/workspace"
import { formatPhone } from "@/lib/utils"
import { toast } from "sonner"

interface ProfileViewProps {
  isMobile: boolean
  profileTab: string
  setProfileTab: (tab: string) => void
  t: Record<string, string>
  user: UserProfile | null
  profileName: string
  setProfileName: (val: string) => void
  profilePhone: string
  setProfilePhone: (val: string) => void
  profileAvatarUrl: string | null
  setProfileAvatarUrl: (val: string) => void
  handleSaveProfile: (e: React.FormEvent) => void
  isLoginMusicEnabled: boolean
  setIsLoginMusicEnabled: (val: boolean) => void
  loginMusicUrl: string
  setLoginMusicUrl: (val: string) => void
  fullSizeScreen?: boolean
  setFullSizeScreen?: (val: boolean) => void
  lang: Language
  currentPassword: string
  setCurrentPassword: (val: string) => void
  newPassword: string
  setNewPassword: (val: string) => void
  confirmPassword: string
  setConfirmPassword: (val: string) => void
  isChangingPassword: boolean
  handleSavePassword: (e: React.FormEvent) => void
  totpDisableCode: string
  setTotpDisableCode: (val: string) => void
  handleDisable2FA: (e?: React.FormEvent) => void
  handleEnable2FA: (e?: React.FormEvent) => void
  setupData: { qrCodeUrl?: string } | null
  totpError: string | null
  totpCode: string
  setTotpCode: (val: string) => void
  lockscreenPin: string
  setLockscreenPin: (val: string) => void
  handleCloseApp: (appId: string) => void
}

export function ProfileView({
  isMobile,
  profileTab,
  setProfileTab,
  t,
  user,
  profileName,
  setProfileName,
  profilePhone,
  setProfilePhone,
  profileAvatarUrl,
  setProfileAvatarUrl,
  handleSaveProfile,
  isLoginMusicEnabled,
  setIsLoginMusicEnabled,
  loginMusicUrl,
  setLoginMusicUrl,
  fullSizeScreen = false,
  setFullSizeScreen,
  lang,
  currentPassword,
  setCurrentPassword,
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword,
  isChangingPassword,
  handleSavePassword,
  totpDisableCode,
  setTotpDisableCode,
  handleDisable2FA,
  handleEnable2FA,
  setupData,
  totpError,
  totpCode,
  setTotpCode,
  lockscreenPin,
  setLockscreenPin,
  handleCloseApp,
}: ProfileViewProps) {
  const DEFAULT_STARTUP_SOUND = "https://cdn.boldworkspace.com.br/storage/v1/object/public/alpha/startup-sound.mp3"

  const [savedCustomSoundUrl, setSavedCustomSoundUrl] = React.useState<string>(() => {
    if (typeof window !== "undefined") {
      const uKey = user?.id ? `customLoginMusicUrl:${user.id}` : "customLoginMusicUrl"
      const stored = localStorage.getItem(uKey) || localStorage.getItem("customLoginMusicUrl")
      if (stored) return stored
    }
    if (loginMusicUrl && loginMusicUrl !== "https://cdn.boldworkspace.com.br/storage/v1/object/public/alpha/startup-sound.mp3") {
      return loginMusicUrl
    }
    return ""
  })

  // Sincroniza savedCustomSoundUrl se loginMusicUrl mudar para um link customizado
  React.useEffect(() => {
    if (loginMusicUrl && loginMusicUrl !== DEFAULT_STARTUP_SOUND) {
      setSavedCustomSoundUrl(loginMusicUrl)
      if (typeof window !== "undefined") {
        if (user?.id) {
          localStorage.setItem(`customLoginMusicUrl:${user.id}`, loginMusicUrl)
        }
        localStorage.setItem("customLoginMusicUrl", loginMusicUrl)
      }
    }
  }, [loginMusicUrl, user?.id])

  // Estados de Verificação do Telefone / WhatsApp
  const [isPhoneVerifyModalOpen, setIsPhoneVerifyModalOpen] = React.useState(false)
  const [phoneVerificationCode, setPhoneVerificationCode] = React.useState("")
  const [phoneVerificationTimer, setPhoneVerificationTimer] = React.useState(180) // 3 minutos
  const [isSendingPhoneCode, setIsSendingPhoneCode] = React.useState(false)
  const [isVerifyingPhoneCode, setIsVerifyingPhoneCode] = React.useState(false)
  const [phoneVerifiedAt, setPhoneVerifiedAt] = React.useState<string | null>(user?.phoneVerifiedAt || null)
  const [phoneVerificationError, setPhoneVerificationError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (user?.phoneVerifiedAt) {
      setPhoneVerifiedAt(user.phoneVerifiedAt)
    }
  }, [user?.phoneVerifiedAt])

  // Timer regressivo de 3 minutos
  React.useEffect(() => {
    let interval: NodeJS.Timeout | null = null
    if (isPhoneVerifyModalOpen && phoneVerificationTimer > 0) {
      interval = setInterval(() => {
        setPhoneVerificationTimer((prev) => (prev > 0 ? prev - 1 : 0))
      }, 1000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isPhoneVerifyModalOpen, phoneVerificationTimer])

  const formatTimeLeft = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
  }

  const handleSendPhoneCode = async () => {
    const rawPhone = profilePhone.replace(/\D/g, "")
    if (rawPhone.length < 10) {
      toast.error("Por favor, digite um número de telefone com DDD válido.")
      return
    }

    const token =
      document.cookie
        .split("; ")
        .find((r) => r.startsWith("token="))
        ?.split("=")[1] || (typeof window !== "undefined" ? localStorage.getItem("bold_token") : null)

    if (!token) {
      toast.error("Sessão expirada. Faça login novamente.")
      return
    }

    setIsSendingPhoneCode(true)
    setPhoneVerificationError(null)

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation SendPhoneCode($phone: String!) {
              sendPhoneVerificationCode(phone: $phone) {
                success
                message
              }
            }
          `,
          variables: { phone: profilePhone }
        })
      })

      const json = await res.json()
      if (json.errors && json.errors.length > 0) {
        throw new Error(json.errors[0].message || "Erro ao enviar código de verificação.")
      }

      if (json.data?.sendPhoneVerificationCode?.success) {
        setIsPhoneVerifyModalOpen(true)
        setPhoneVerificationTimer(180)
        setPhoneVerificationCode("")
        setPhoneVerificationError(null)
        toast.success("Código de 6 dígitos enviado para seu WhatsApp!")
      }
    } catch (err: any) {
      toast.error(err.message || "Não foi possível enviar o código via WhatsApp.")
    } finally {
      setIsSendingPhoneCode(false)
    }
  }

  const handleVerifyPhoneCode = async () => {
    if (phoneVerificationCode.trim().length !== 6) {
      setPhoneVerificationError("Digite o código completo de 6 dígitos.")
      return
    }

    const token =
      document.cookie
        .split("; ")
        .find((r) => r.startsWith("token="))
        ?.split("=")[1] || (typeof window !== "undefined" ? localStorage.getItem("bold_token") : null)

    if (!token) {
      toast.error("Sessão expirada. Faça login novamente.")
      return
    }

    setIsVerifyingPhoneCode(true)
    setPhoneVerificationError(null)

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation VerifyCode($phone: String!, $code: String!) {
              verifyPhoneCode(phone: $phone, code: $code) {
                success
                message
                phoneVerifiedAt
              }
            }
          `,
          variables: {
            phone: profilePhone,
            code: phoneVerificationCode.trim()
          }
        })
      })

      const json = await res.json()
      if (json.errors && json.errors.length > 0) {
        throw new Error(json.errors[0].message || "Código inválido ou expirado.")
      }

      const result = json.data?.verifyPhoneCode
      if (result?.success) {
        const verifiedTimestamp = result.phoneVerifiedAt || new Date().toISOString()
        setPhoneVerifiedAt(verifiedTimestamp)
        if (user) {
          user.phoneVerifiedAt = verifiedTimestamp
          user.phone = profilePhone
        }
        setIsPhoneVerifyModalOpen(false)
        toast.success("Telefone e WhatsApp verificados com sucesso!")
      }
    } catch (err: any) {
      setPhoneVerificationError(err.message || "Erro ao validar código.")
      toast.error(err.message || "Código inválido.")
    } finally {
      setIsVerifyingPhoneCode(false)
    }
  }

  return (
    <Tabs
      orientation={isMobile ? "horizontal" : "vertical"}
      value={profileTab}
      onValueChange={(value: any) => setProfileTab(value)}
      className="flex flex-col md:flex-row flex-1 h-full overflow-hidden"
    >
      {/* Sidebar Esquerda (Aba de Navegação) */}
      {(!isMobile || profileTab === "menu") && (
        <div className="w-full md:w-60 bg-zinc-50 dark:bg-zinc-950 border-r border-zinc-150 dark:border-zinc-800 p-4 flex flex-col justify-between shrink-0 select-none font-sans h-full">
          {isMobile ? (
            <div className="flex-1 flex flex-col justify-between h-full">
              <div className="space-y-6">
                <div className="flex items-center gap-3 px-1 py-2">
                  <div className="flex size-11 items-center justify-center rounded-xl bg-gradient-to-tr from-[#004EB1] to-blue-500 shadow-md shadow-blue-500/20 text-white">
                    <User className="size-5.5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-extrabold text-zinc-900 dark:text-white leading-tight">
                      Ajustes do Perfil
                    </h3>
                    <p className="text-[10px] text-zinc-450 dark:text-zinc-550 mt-0.5">
                      Gerencie sua conta e preferências
                    </p>
                  </div>
                </div>

                <div className="space-y-3 pt-2">
                  <p className="text-[9px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider px-1">
                    Configurações
                  </p>
                  <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200/60 dark:border-zinc-800/80 overflow-hidden divide-y divide-zinc-100 dark:divide-zinc-800">
                    <button
                      type="button"
                      onClick={() => setProfileTab("profile")}
                      className="w-full flex items-center justify-between p-4 hover:bg-zinc-50 dark:hover:bg-zinc-950 active:bg-zinc-100 dark:active:bg-zinc-800 transition-all text-left cursor-pointer group"
                    >
                      <div className="flex items-center gap-3.5">
                        <div className="flex size-9 items-center justify-center rounded-xl bg-blue-500/10 text-[#004EB1] dark:bg-blue-950/40 dark:text-blue-400">
                          <User className="size-4.5" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-zinc-900 dark:text-zinc-200">Dados Pessoais</p>
                          <p className="text-[9px] text-zinc-400 dark:text-zinc-500 mt-0.5">Nome, foto e login</p>
                        </div>
                      </div>
                      <ChevronRight className="size-4 text-zinc-400 dark:text-zinc-600 group-hover:text-zinc-600 transition-colors" />
                    </button>



                    <button
                      type="button"
                      onClick={() => setProfileTab("personalization")}
                      className="w-full flex items-center justify-between p-4 hover:bg-zinc-50 dark:hover:bg-zinc-950 active:bg-zinc-100 dark:active:bg-zinc-800 transition-all text-left cursor-pointer group"
                    >
                      <div className="flex items-center gap-3.5">
                        <div className="flex size-9 items-center justify-center rounded-xl bg-purple-500/10 text-purple-600 dark:bg-purple-950/40 dark:text-purple-400">
                          <Palette className="size-4.5" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-zinc-900 dark:text-zinc-200">Personalização</p>
                          <p className="text-[9px] text-zinc-400 dark:text-zinc-500 mt-0.5">Wallpaper e som do sistema</p>
                        </div>
                      </div>
                      <ChevronRight className="size-4 text-zinc-400 dark:text-zinc-600 group-hover:text-zinc-600 transition-colors" />
                    </button>

                    <button
                      type="button"
                      onClick={() => setProfileTab("security")}
                      className="w-full flex items-center justify-between p-4 hover:bg-zinc-50 dark:hover:bg-zinc-950 active:bg-zinc-100 dark:active:bg-zinc-800 transition-all text-left cursor-pointer group"
                    >
                      <div className="flex items-center gap-3.5">
                        <div className="flex size-9 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400">
                          <Shield className="size-4.5" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-zinc-900 dark:text-zinc-200">Segurança & Acesso</p>
                          <p className="text-[9px] text-zinc-400 dark:text-zinc-500 mt-0.5">PIN rápido e 2FA</p>
                        </div>
                      </div>
                      <ChevronRight className="size-4 text-zinc-400 dark:text-zinc-600 group-hover:text-zinc-600 transition-colors" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-zinc-200 dark:border-zinc-800">
                <Button
                  type="button"
                  variant="outline"
                  className="text-xs h-10 px-4 font-semibold w-full cursor-pointer rounded-xl"
                  onClick={() => handleCloseApp("profile")}
                >
                  Fechar Ajustes
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div className="flex items-center gap-2 px-2 py-1">
                  <div className="flex size-7 items-center justify-center rounded bg-[#004EB1]">
                    <User className="size-4 text-white" />
                  </div>
                  <span className="font-bold text-xs text-zinc-900 dark:text-white uppercase tracking-wider">
                    {t.myProfileTitle || "Meu Perfil"}
                  </span>
                </div>

                <div className="space-y-4 text-[10px]">
                  <div className="space-y-1">
                    <div className="px-3 text-[9px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">
                      {t.settingsCategoryPersonal}
                    </div>
                    <TabsList className="flex flex-col gap-0.5 w-full bg-transparent border-0 p-0 h-auto">
                      <TabsTrigger
                        value="profile"
                        className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${profileTab === "profile"
                          ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                          : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                          }`}
                      >
                        <User className="size-4 shrink-0" />
                        {t.settingsTabProfile}
                      </TabsTrigger>



                      <TabsTrigger
                        value="personalization"
                        className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${profileTab === "personalization"
                          ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                          : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                          }`}
                      >
                        <Palette className="size-4 shrink-0" />
                        Personalização
                      </TabsTrigger>

                      <TabsTrigger
                        value="security"
                        className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${profileTab === "security"
                          ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                          : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                          }`}
                      >
                        <Shield className="size-4 shrink-0" />
                        {t.settingsTabSecurity}
                      </TabsTrigger>
                    </TabsList>
                  </div>
                </div>
              </div>

            </>
          )}
        </div>
      )}

      {/* Painel da Direita (Conteúdo com scroll) */}
      {(!isMobile || profileTab !== "menu") && (
        <div className="flex-1 bg-white dark:bg-zinc-900 p-6 overflow-y-auto h-full">
          {isMobile && (
            <button
              onClick={() => setProfileTab("menu")}
              className="mb-4 flex items-center gap-1.5 text-xs font-bold text-[#004EB1] dark:text-blue-400 bg-blue-500/10 dark:bg-blue-500/20 px-3 py-1.5 rounded-lg w-fit transition-colors cursor-pointer"
            >
              ← Voltar
            </button>
          )}

          {/* 1. Aba Perfil */}
          <TabsContent value="profile" className="outline-none space-y-6">
            <div>
              <h3 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                <User className="size-5 text-[#004EB1]" />
                Meu Perfil
              </h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-sans">
                Gerencie suas informações de perfil pessoal no Bold Workspace.
              </p>
            </div>

            <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-xs">
              <CardHeader className="pb-4">
                <CardTitle className="text-sm font-bold text-zinc-800 dark:text-zinc-100 font-sans">
                  Dados Pessoais
                </CardTitle>
                <CardDescription className="text-xs font-sans">
                  Suas credenciais básicas de acesso e identificação.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveProfile} className="space-y-6">
                  {/* Foto de Perfil à esquerda e campos alinhados no mesmo bloco */}
                  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                    {/* Avatar Interativo com clique para upload */}
                    <div className="flex flex-col items-center gap-2 shrink-0">
                      <SupabaseUpload
                        mode="custom"
                        currentUrl={profileAvatarUrl || ""}
                        onUploadSuccess={(url: string) => setProfileAvatarUrl(url)}
                        cropShape="circle"
                      >
                        {(trigger, uploading) => (
                          <div
                            onClick={trigger}
                            className="group relative size-28 sm:size-32 rounded-2xl cursor-pointer select-none ring-2 ring-zinc-200 dark:ring-zinc-800 shadow-md hover:shadow-xl hover:ring-[#004EB1]/50 transition-all duration-300 overflow-hidden"
                            title="Clique para alterar a foto de perfil"
                          >
                            {profileAvatarUrl ? (
                              <img
                                src={profileAvatarUrl}
                                alt={profileName || "Avatar"}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-[#004EB1] to-[#002D66] text-white flex items-center justify-center text-3xl font-bold shadow-inner">
                                {profileName ? profileName.charAt(0).toUpperCase() : <User className="size-12" />}
                              </div>
                            )}

                            {/* Overlay de hover com ícone e texto */}
                            <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-all duration-200 flex flex-col items-center justify-center text-white gap-1 text-center p-2">
                              <Camera className="size-6 text-white drop-shadow-md animate-in zoom-in-75 duration-150" />
                              <span className="text-[10px] font-bold tracking-tight uppercase">Alterar Foto</span>
                            </div>

                            {/* Indicador de carregamento */}
                            {uploading && (
                              <div className="absolute inset-0 bg-black/75 backdrop-blur-xs flex flex-col items-center justify-center text-white gap-1">
                                <Loader2 className="size-6 animate-spin text-white" />
                                <span className="text-[10px] font-semibold">Enviando...</span>
                              </div>
                            )}
                          </div>
                        )}
                      </SupabaseUpload>
                    </div>

                    {/* Campos do Formulário em Grid Responsivo ao lado */}
                    <div className="flex-1 w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Nome Completo */}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between h-5">
                          <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider font-sans">
                            Nome Completo
                          </label>
                        </div>
                        <input
                          type="text"
                          value={profileName}
                          onChange={(e) => setProfileName(e.target.value)}
                          placeholder="Digite seu nome completo"
                          required
                          className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-xs bg-white focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] dark:bg-zinc-950 dark:border-zinc-800 text-zinc-900 dark:text-white font-sans focus:outline-none transition-all"
                        />
                      </div>

                      {/* Telefone / WhatsApp */}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between h-5">
                          <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider font-sans">
                            Telefone / WhatsApp
                          </label>
                          {Boolean(phoneVerifiedAt) && profilePhone === user?.phone ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full border border-emerald-200/80 dark:border-emerald-800/80 shadow-2xs">
                              <CheckCircle2 className="size-3 text-emerald-600 dark:text-emerald-400" />
                              Verificado
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleSendPhoneCode()}
                              disabled={isSendingPhoneCode || !profilePhone || profilePhone.replace(/\D/g, "").length < 10}
                              className="inline-flex items-center gap-1 text-[10px] font-bold text-[#004EB1] hover:text-[#003d8d] dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-40 disabled:cursor-not-allowed hover:underline cursor-pointer transition-colors"
                              title={!profilePhone || profilePhone.replace(/\D/g, "").length < 10 ? "Digite o número de telefone completo" : "Enviar código de verificação via WhatsApp"}
                            >
                              {isSendingPhoneCode ? (
                                <>
                                  <Loader2 className="size-3 animate-spin" />
                                  <span>Enviando código...</span>
                                </>
                              ) : (
                                <>
                                  <ShieldCheck className="size-3.5" />
                                  <span>Verificar número</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                        <input
                          type="tel"
                          value={profilePhone}
                          onChange={(e) => setProfilePhone(formatPhone(e.target.value))}
                          placeholder="(11) 99999-9999"
                          className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-xs bg-white focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] dark:bg-zinc-950 dark:border-zinc-800 text-zinc-900 dark:text-white font-sans focus:outline-none transition-all"
                        />
                      </div>

                      {/* E-mail (ocupando largura inteira quando em 2 colunas) */}
                      <div className="space-y-1.5 sm:col-span-2">
                        <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider font-sans">
                          E-mail
                        </label>
                        <input
                          type="email"
                          disabled
                          readOnly
                          value={user?.email || ""}
                          className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-xs bg-zinc-50 dark:bg-zinc-950 dark:border-zinc-800 text-zinc-400 dark:text-zinc-500 cursor-not-allowed font-sans"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-3 border-t border-zinc-100 dark:border-zinc-800">
                    <Button
                      type="submit"
                      className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-9 px-4 font-sans cursor-pointer transition-all shadow-xs"
                    >
                      Salvar Perfil
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Modal Profissional de Verificação de WhatsApp (Timer de 3 minutos) */}
            {isPhoneVerifyModalOpen && (
              <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-md p-4 animate-in fade-in duration-200">
                <div className="relative w-full max-w-md bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                  {/* Cabeçalho */}
                  <div className="p-5 border-b border-zinc-100 dark:border-zinc-800 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="size-9 rounded-xl bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                        <Smartphone className="size-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-zinc-900 dark:text-white">Verificar WhatsApp</h3>
                        <p className="text-[11px] text-zinc-500 dark:text-zinc-400">Confirmação de segurança do número</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsPhoneVerifyModalOpen(false)}
                      className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 text-sm font-bold p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 cursor-pointer transition-colors"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Corpo do Modal */}
                  <div className="p-6 space-y-5">
                    <div className="text-center space-y-1.5">
                      <p className="text-xs text-zinc-600 dark:text-zinc-300">
                        Enviamos um código de <span className="font-bold text-zinc-900 dark:text-white">6 dígitos</span> para o seu WhatsApp:
                      </p>
                      <p className="text-sm font-bold text-[#004EB1] dark:text-blue-400 tracking-wide font-mono bg-blue-50/50 dark:bg-blue-950/30 py-1 px-3 rounded-lg inline-block border border-blue-100 dark:border-blue-900/50">
                        {profilePhone}
                      </p>
                    </div>

                    {/* Timer Badge (3 Minutos) */}
                    <div className="flex items-center justify-center">
                      <div
                        className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${phoneVerificationTimer > 0
                            ? "bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900/80"
                            : "bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-900/80 animate-pulse"
                          }`}
                      >
                        <Clock className="size-3.5" />
                        {phoneVerificationTimer > 0 ? (
                          <span>
                            Tempo restante: <strong>{formatTimeLeft(phoneVerificationTimer)}</strong>
                          </span>
                        ) : (
                          <span>Código expirado. Solicite o reenvio.</span>
                        )}
                      </div>
                    </div>

                    {/* Input do Código */}
                    <div className="space-y-2">
                      <label className="text-[11px] font-bold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider block text-center">
                        Digite o código recebido
                      </label>
                      <input
                        type="text"
                        maxLength={6}
                        autoFocus
                        value={phoneVerificationCode}
                        onChange={(e) => {
                          setPhoneVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                          if (phoneVerificationError) setPhoneVerificationError(null)
                        }}
                        placeholder="------"
                        className="w-full text-center text-2xl font-bold tracking-[0.4em] py-3 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-white focus:outline-none focus:border-[#004EB1] focus:ring-2 focus:ring-[#004EB1]/20 font-mono transition-all"
                      />
                      {phoneVerificationError && (
                        <p className="text-[11px] text-red-500 font-medium text-center">
                          {phoneVerificationError}
                        </p>
                      )}
                    </div>

                    {/* Ações */}
                    <div className="space-y-3 pt-2">
                      <Button
                        type="button"
                        onClick={handleVerifyPhoneCode}
                        disabled={isVerifyingPhoneCode || phoneVerificationCode.length !== 6 || phoneVerificationTimer === 0}
                        className="w-full bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-10 rounded-xl cursor-pointer shadow-md transition-all disabled:opacity-50"
                      >
                        {isVerifyingPhoneCode ? (
                          <>
                            <Loader2 className="size-4 animate-spin mr-2" />
                            Confirmando...
                          </>
                        ) : (
                          "Confirmar Código"
                        )}
                      </Button>

                      <div className="text-center">
                        <button
                          type="button"
                          onClick={() => handleSendPhoneCode()}
                          disabled={isSendingPhoneCode || phoneVerificationTimer > 150}
                          className="text-[11px] text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200 hover:underline disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                        >
                          {isSendingPhoneCode
                            ? "Reenviando..."
                            : phoneVerificationTimer > 150
                              ? `Aguarde ${phoneVerificationTimer - 150}s para reenviar`
                              : "Não recebeu? Clique para reenviar código"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>



          {/* 2. Aba Personalização */}
          <TabsContent value="personalization" className="outline-none space-y-6">
            <div>
              <h3 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                <Palette className="size-5 text-[#004EB1]" />
                Personalização do Usuário
              </h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-sans">
                Ajuste o som de inicialização e preferências de visualização da sua conta.
              </p>
            </div>

            {/* Modo de Tela Cheia */}
            <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
              <CardHeader>
                <CardTitle className="text-sm font-bold text-zinc-800 dark:text-zinc-100 font-sans flex items-center gap-2">
                  <Maximize2 className="size-4 text-[#004EB1]" />
                  Modo de Tela Cheia
                </CardTitle>
                <CardDescription className="text-xs font-sans">
                  Configure se a sugestão para expandir a área de trabalho deve ser exibida ao inicializar o Bold Workspace.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-zinc-50 dark:bg-zinc-950 rounded-xl border border-zinc-200/60 dark:border-zinc-800/80">
                  <div className="space-y-0.5 pr-4">
                    <span className="text-xs font-bold text-zinc-700 dark:text-zinc-300 block">
                      Não exibir sugestão de tela cheia
                    </span>
                    <span className="text-[11px] text-zinc-500 dark:text-zinc-400 block font-sans">
                      {fullSizeScreen
                        ? "Sugestão desativada (a sugestão de tela cheia não será mais exibida ao carregar)."
                        : "Sugestão ativa (o sistema sugerirá tela cheia se a janela não estiver expandida ao carregar)."}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const next = !fullSizeScreen
                      setFullSizeScreen?.(next)
                      if (user?.id) {
                        localStorage.setItem(`fullSizeScreen:${user.id}`, String(next))
                      }
                      localStorage.setItem("bold_fullscreen_dismissed_permanent", String(next))
                      const token = document.cookie
                        .split("; ")
                        .find((r) => r.startsWith("token="))
                        ?.split("=")[1]
                      if (token && user) {
                        fetch("/api/graphql", {
                          method: "POST",
                          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                          body: JSON.stringify({
                            query: `
                              mutation UpdateUserProfile($name: String!, $fullSizeScreen: Boolean) {
                                updateUserProfile(name: $name, fullSizeScreen: $fullSizeScreen) {
                                  id
                                  fullSizeScreen
                                }
                              }
                            `,
                            variables: { name: user.name || profileName, fullSizeScreen: next }
                          })
                        })
                          .then((res) => res.json())
                          .then((data) => {
                            if (data?.data?.updateUserProfile) {
                              toast.success(
                                next
                                  ? "Sugestão de tela cheia desativada."
                                  : "Sugestão de tela cheia ativada ao carregar!"
                              )
                            }
                          })
                          .catch(() => { })
                      }
                    }}
                    className={`relative inline-flex h-5.5 w-10 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${fullSizeScreen ? "bg-[#004EB1]" : "bg-zinc-300 dark:bg-zinc-700"
                      }`}
                  >
                    <span
                      className={`pointer-events-none inline-block size-4.5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${fullSizeScreen ? "translate-x-4.5" : "translate-x-0"
                        }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs text-zinc-500 dark:text-zinc-400 font-sans">
                    Você também pode alternar o modo tela cheia agora:
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (!document.fullscreenElement) {
                        document.documentElement.requestFullscreen().catch(() => {
                          toast.error("Não foi possível entrar em modo tela cheia.")
                        })
                      } else {
                        document.exitFullscreen().catch(() => { })
                      }
                    }}
                    className="text-xs font-semibold h-8 px-3 cursor-pointer flex items-center gap-1.5"
                  >
                    <Maximize2 className="size-3.5" />
                    <span>Alternar Tela Cheia</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Som de Inicialização */}
            <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
              <CardHeader>
                <CardTitle className="text-sm font-bold text-zinc-800 dark:text-zinc-100 font-sans flex items-center gap-2">
                  <Volume2 className="size-4 text-[#004EB1]" />
                  Som de Inicialização
                </CardTitle>
                <CardDescription className="text-xs font-sans">
                  Configure uma música ou efeito sonoro premium para tocar ao desbloquear a sua área de trabalho.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-zinc-50 dark:bg-zinc-950 rounded-xl border border-zinc-200/60 dark:border-zinc-800/80">
                  <span className="text-xs font-bold text-zinc-700 dark:text-zinc-300">Tocar som ao fazer login</span>
                  <button
                    type="button"
                    onClick={() => {
                      const next = !isLoginMusicEnabled
                      setIsLoginMusicEnabled(next)
                      if (user?.id) {
                        localStorage.setItem(`loginMusicEnabled:${user.id}`, String(next))
                      }
                      const token = document.cookie
                        .split("; ")
                        .find((r) => r.startsWith("token="))
                        ?.split("=")[1]
                      if (token && user) {
                        fetch("/api/graphql", {
                          method: "POST",
                          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                          body: JSON.stringify({
                            query: `
                              mutation UpdateUserProfile($name: String!, $loginMusicEnabled: Boolean) {
                                updateUserProfile(name: $name, loginMusicEnabled: $loginMusicEnabled) {
                                  id
                                  loginMusicEnabled
                                }
                              }
                            `,
                            variables: { name: user.name || profileName, loginMusicEnabled: next }
                          })
                        }).catch(() => { })
                      }
                    }}
                    className={`relative inline-flex h-5.5 w-10 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isLoginMusicEnabled ? "bg-[#004EB1]" : "bg-zinc-300 dark:bg-zinc-700"
                      }`}
                  >
                    <span
                      className={`pointer-events-none inline-block size-4.5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isLoginMusicEnabled ? "translate-x-4.5" : "translate-x-0"
                        }`}
                    />
                  </button>
                </div>

                {isLoginMusicEnabled && (
                  <div className="space-y-3 p-4 bg-zinc-50 dark:bg-zinc-950 rounded-xl border border-zinc-200/60 dark:border-zinc-800/80 font-sans">
                    <label className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase block">
                      Origem do Áudio
                    </label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={() => {
                          setLoginMusicUrl(DEFAULT_STARTUP_SOUND)
                          if (user?.id) {
                            localStorage.setItem(`loginMusicUrl:${user.id}`, DEFAULT_STARTUP_SOUND)
                            localStorage.setItem("loginMusicUrl", DEFAULT_STARTUP_SOUND)
                          }
                          const token = document.cookie
                            .split("; ")
                            .find((r) => r.startsWith("token="))
                            ?.split("=")[1]
                          if (token && user) {
                            fetch("/api/graphql", {
                              method: "POST",
                              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                              body: JSON.stringify({
                                query: `
                                  mutation UpdateUserProfile($name: String!, $loginMusicUrl: String) {
                                    updateUserProfile(name: $name, loginMusicUrl: $loginMusicUrl) {
                                      id
                                      loginMusicUrl
                                    }
                                  }
                                `,
                                variables: { name: user.name || profileName, loginMusicUrl: DEFAULT_STARTUP_SOUND }
                              })
                            }).catch(() => { })
                          }
                        }}
                        variant={loginMusicUrl === DEFAULT_STARTUP_SOUND ? "default" : "outline"}
                        className="text-xs font-semibold h-8 px-3 cursor-pointer"
                      >
                        Som Padrão (Bold)
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          const targetUrl = savedCustomSoundUrl || ""
                          setLoginMusicUrl(targetUrl)
                          if (user?.id) {
                            localStorage.setItem(`loginMusicUrl:${user.id}`, targetUrl)
                            localStorage.setItem("loginMusicUrl", targetUrl)
                          }
                          const token = document.cookie
                            .split("; ")
                            .find((r) => r.startsWith("token="))
                            ?.split("=")[1]
                          if (token && user && targetUrl) {
                            fetch("/api/graphql", {
                              method: "POST",
                              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                              body: JSON.stringify({
                                query: `
                                  mutation UpdateUserProfile($name: String!, $loginMusicUrl: String) {
                                    updateUserProfile(name: $name, loginMusicUrl: $loginMusicUrl) {
                                      id
                                      loginMusicUrl
                                    }
                                  }
                                `,
                                variables: { name: user.name || profileName, loginMusicUrl: targetUrl }
                              })
                            }).catch(() => { })
                          }
                        }}
                        variant={loginMusicUrl !== DEFAULT_STARTUP_SOUND ? "default" : "outline"}
                        className="text-xs font-semibold h-8 px-3 cursor-pointer"
                      >
                        Som Personalizado
                      </Button>
                    </div>

                    {loginMusicUrl !== DEFAULT_STARTUP_SOUND && (
                      <div className="space-y-2 pt-2 animate-in fade-in duration-200">
                        <label className="text-[10px] font-bold text-zinc-400 dark:text-zinc-550 uppercase block">
                          Upload do Som Personalizado
                        </label>
                        {loginMusicUrl ? (
                          <Attachment className="mt-1">
                            <AttachmentMedia>
                              <FileAudio className="size-5 text-[#004EB1] dark:text-blue-400" />
                            </AttachmentMedia>
                            <AttachmentContent>
                              <AttachmentTitle>
                                {loginMusicUrl.substring(loginMusicUrl.lastIndexOf("/") + 1) ||
                                  "som-personalizado.mp3"}
                              </AttachmentTitle>
                              <AttachmentDescription>
                                {lang === "pt"
                                  ? "Som personalizado ativo"
                                  : lang === "es"
                                    ? "Sonido personalizado activo"
                                    : "Custom sound active"}
                              </AttachmentDescription>
                            </AttachmentContent>
                            <AttachmentActions>
                              <AttachmentAction
                                onClick={() => {
                                  setLoginMusicUrl("")
                                  setSavedCustomSoundUrl("")
                                  if (user?.id) {
                                    localStorage.removeItem(`loginMusicUrl:${user.id}`)
                                    localStorage.removeItem(`customLoginMusicUrl:${user.id}`)
                                  }
                                  localStorage.removeItem("loginMusicUrl")
                                  localStorage.removeItem("customLoginMusicUrl")
                                  const token = document.cookie
                                    .split("; ")
                                    .find((r) => r.startsWith("token="))
                                    ?.split("=")[1]
                                  if (token && user) {
                                    fetch("/api/graphql", {
                                      method: "POST",
                                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                      body: JSON.stringify({
                                        query: `
                                          mutation UpdateUserProfile($name: String!, $loginMusicUrl: String) {
                                            updateUserProfile(name: $name, loginMusicUrl: $loginMusicUrl) {
                                              id
                                              loginMusicUrl
                                            }
                                          }
                                        `,
                                        variables: { name: user.name || profileName, loginMusicUrl: "" }
                                      })
                                    }).catch(() => { })
                                  }
                                  toast.info("Som personalizado removido. Você pode carregar um novo arquivo.")
                                }}
                                title={
                                  lang === "pt"
                                    ? "Remover arquivo"
                                    : lang === "es"
                                      ? "Eliminar archivo"
                                      : "Remove file"
                                }
                              >
                                <X className="size-4" />
                              </AttachmentAction>
                            </AttachmentActions>
                          </Attachment>
                        ) : (
                          <SupabaseUpload
                            label="Selecione ou arraste um arquivo de áudio (MP3 ou WAV)"
                            accept=".mp3,.wav,audio/mpeg,audio/wav,audio/x-wav"
                            disableCrop={true}
                            currentUrl={loginMusicUrl}
                            onUploadSuccess={(url: string) => {
                              setLoginMusicUrl(url)
                              setSavedCustomSoundUrl(url)
                              if (user?.id) {
                                localStorage.setItem(`loginMusicUrl:${user.id}`, url)
                                localStorage.setItem(`customLoginMusicUrl:${user.id}`, url)
                                localStorage.setItem("loginMusicUrl", url)
                                localStorage.setItem("customLoginMusicUrl", url)
                              }
                              const token = document.cookie
                                .split("; ")
                                .find((r) => r.startsWith("token="))
                                ?.split("=")[1]
                              if (token && user) {
                                fetch("/api/graphql", {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                  body: JSON.stringify({
                                    query: `
                                      mutation UpdateUserProfile($name: String!, $loginMusicUrl: String) {
                                        updateUserProfile(name: $name, loginMusicUrl: $loginMusicUrl) {
                                          id
                                          loginMusicUrl
                                        }
                                      }
                                    `,
                                    variables: { name: user.name || profileName, loginMusicUrl: url }
                                  })
                                }).catch(() => { })
                              }
                              toast.success("Áudio personalizado salvo com sucesso!")
                            }}
                          />
                        )}
                      </div>
                    )}

                    <div className="flex justify-end pt-2">
                      <Button
                        type="button"
                        onClick={() => {
                          try {
                            if (!loginMusicUrl) {
                              toast.error("Nenhum som selecionado ou carregado.")
                              return
                            }
                            const audio = new Audio(loginMusicUrl)
                            audio.volume = 0.35
                            audio.play()
                            toast.success("Testando som...")
                          } catch (e) {
                            toast.error("Erro ao reproduzir o som.")
                          }
                        }}
                        className="bg-zinc-700 hover:bg-zinc-800 text-white text-xs font-semibold h-8 px-3 cursor-pointer"
                      >
                        Testar Som
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* 3. Aba Segurança */}
          <TabsContent value="security" className="outline-none space-y-6">
            <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="space-y-1 flex-1">
                    <h3 className="text-sm font-bold text-zinc-900 dark:text-white">Nível de Segurança da Conta</h3>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">
                      {user?.twoFactorEnabled
                        ? "Parabéns! Sua conta corporativa está com o nível máximo de proteção ativado."
                        : "Atenção: Ative a autenticação de dois fatores (2FA) para atingir 100% de segurança."}
                    </p>
                  </div>
                  <div className="w-full md:w-72 shrink-0">
                    <div className="w-full bg-zinc-100 dark:bg-zinc-800 h-2.5 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-500 rounded-full ${user?.twoFactorEnabled ? "bg-emerald-500 w-full" : "bg-amber-500 w-1/3"
                          }`}
                      />
                    </div>
                    <div className="flex justify-between items-center mt-1 text-[10px] font-bold text-zinc-400 dark:text-zinc-500 font-sans">
                      <span>{user?.twoFactorEnabled ? "100% Protegido" : "33% Vulnerável"}</span>
                      <span>{user?.twoFactorEnabled ? "Forte" : "Fraco"}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
              <CardHeader>
                <CardTitle className="text-sm font-bold text-zinc-800 dark:text-zinc-100 font-sans">
                  {t.securityCardPassword}
                </CardTitle>
                <CardDescription className="text-xs font-sans">
                  Atualize sua senha de acesso periodicamente.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSavePassword} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider font-sans">
                      {t.currentPassword}
                    </label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="w-full rounded-md border border-zinc-200 px-3 py-2 text-xs bg-white focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] dark:bg-zinc-950 dark:border-zinc-800 text-zinc-900 dark:text-white font-sans focus:outline-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider font-sans">
                      {t.newPassword}
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="w-full rounded-md border border-zinc-200 px-3 py-2 text-xs bg-white focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] dark:bg-zinc-950 dark:border-zinc-800 text-zinc-900 dark:text-white font-sans focus:outline-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider font-sans">
                      {t.confirmNewPassword}
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="w-full rounded-md border border-zinc-200 px-3 py-2 text-xs bg-white focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] dark:bg-zinc-950 dark:border-zinc-800 text-zinc-900 dark:text-white font-sans focus:outline-none"
                    />
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button
                      type="submit"
                      disabled={isChangingPassword}
                      className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-9 px-4 font-sans cursor-pointer"
                    >
                      {isChangingPassword ? t.saving : t.changePasswordBtn}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
              <CardHeader>
                <CardTitle className="text-sm font-bold text-zinc-800 dark:text-zinc-100 font-sans">
                  {t.securityCardMfa}
                </CardTitle>
                <CardDescription className="text-xs font-sans">
                  Adicione uma camada extra de segurança à sua conta corporativa.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {user?.twoFactorEnabled ? (
                  <div className="space-y-4">
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900 rounded-xl text-xs text-emerald-800 dark:text-emerald-400 font-sans flex items-start gap-2.5">
                      <CheckCircle2 className="size-4 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold">2FA Ativado com Sucesso</p>
                        <p className="mt-0.5 text-[11px] leading-relaxed">
                          Sua conta está protegida por verificação em duas etapas nas tentativas de login.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3 p-4 bg-zinc-50 dark:bg-zinc-950 rounded-xl border border-zinc-200/60 dark:border-zinc-800/80">
                      <label className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase block">
                        {lang === "pt" ? "Código de Segurança para Desativar" : "Security Code to Disable"}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          maxLength={6}
                          placeholder="000000"
                          value={totpDisableCode}
                          onChange={(e) => setTotpDisableCode(e.target.value.replace(/[^0-9]/g, ""))}
                          className="w-32 rounded-md border border-zinc-200 px-3 py-1.5 text-xs text-center font-mono font-bold bg-white focus-visible:outline-none focus-visible:border-red-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-white"
                        />
                        <Button
                          onClick={() => handleDisable2FA()}
                          className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold h-8.5 px-4 cursor-pointer"
                        >
                          {t.disable2faBtn}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {!setupData ? (
                      <Button
                        onClick={() => handleEnable2FA()}
                        className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-9 px-4 cursor-pointer"
                      >
                        {t.enable2faBtn}
                      </Button>
                    ) : (
                      <div className="space-y-4 p-4 border border-zinc-200 dark:border-zinc-800 rounded-xl bg-zinc-50 dark:bg-zinc-950">
                        <div className="flex flex-col md:flex-row gap-4 items-center">
                          {setupData?.qrCodeUrl && (
                            <div className="p-2 bg-white rounded-lg border border-zinc-150 shrink-0">
                              <img
                                src={
                                  setupData.qrCodeUrl.startsWith("http")
                                    ? setupData.qrCodeUrl
                                    : `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(setupData.qrCodeUrl)}`
                                }
                                alt="2FA QR Code"
                                className="size-32"
                              />
                            </div>
                          )}
                          <div className="space-y-1.5 text-xs text-zinc-600 dark:text-zinc-400">
                            <p className="font-bold text-zinc-800 dark:text-zinc-200">1. Escaneie o QR Code</p>
                            <p className="text-[11px] leading-relaxed">
                              Abra seu aplicativo autenticador (Google Authenticator, Microsoft Authenticator ou similar)
                              e escaneie o código ao lado.
                            </p>
                            <p className="font-bold text-zinc-800 dark:text-zinc-200 mt-2">
                              2. Digite o código gerado
                            </p>
                            <p className="text-[11px] leading-relaxed">
                              Insira o código de 6 dígitos gerado pelo aplicativo no campo abaixo para confirmar.
                            </p>
                          </div>
                        </div>

                        {totpError && (
                          <p className="text-[11px] font-bold text-red-500 dark:text-red-400">{totpError}</p>
                        )}

                        <div className="flex gap-2 items-end pt-2">
                          <div className="space-y-1">
                            <label className="text-[9px] font-bold text-zinc-400 uppercase block font-mono">
                              Código de 6 Dígitos
                            </label>
                            <input
                              type="text"
                              maxLength={6}
                              placeholder="000000"
                              value={totpCode}
                              onChange={(e) => setTotpCode(e.target.value.replace(/[^0-9]/g, ""))}
                              className="w-32 rounded-md border border-zinc-200 px-3 py-1.5 text-xs text-center font-mono font-bold bg-white focus-visible:outline-none focus-visible:border-[#004EB1] dark:border-zinc-800 dark:bg-zinc-900 dark:text-white"
                            />
                          </div>
                          <Button
                            onClick={() => handleEnable2FA()}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold h-8.5 px-4 cursor-pointer"
                          >
                            Confirmar Ativação
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 4. PIN de Desbloqueio Rápido */}
            <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
              <CardHeader>
                <CardTitle className="text-sm font-bold text-zinc-800 dark:text-zinc-100 font-sans flex items-center gap-2">
                  <KeyRound className="size-4 text-[#004EB1]" />
                  PIN de Desbloqueio Rápido
                </CardTitle>
                <CardDescription className="text-xs font-sans">
                  Use um PIN numérico simples no seletor de usuários da tela de bloqueio em vez de redigitar toda a sua
                  senha.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-zinc-50 dark:bg-zinc-950 rounded-xl border border-zinc-200/60 dark:border-zinc-800/80 space-y-3">
                  <label className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase block">
                    Definir Novo PIN (4 Dígitos)
                  </label>
                  <div className="flex gap-3 items-center">
                    <input
                      type="password"
                      maxLength={4}
                      placeholder="••••"
                      value={lockscreenPin}
                      onChange={(e) => setLockscreenPin(e.target.value.replace(/[^0-9]/g, ""))}
                      className="w-24 rounded-md border border-zinc-200 px-3 py-2 text-center text-lg font-bold bg-white focus-visible:outline-none focus-visible:border-[#004EB1] dark:border-zinc-800 dark:bg-zinc-900 dark:text-white tracking-widest font-mono"
                    />
                    <Button
                      type="button"
                      disabled={lockscreenPin.length !== 4}
                      onClick={async () => {
                        if (user) {
                          localStorage.setItem(`lockscreenPin:${user.id}`, lockscreenPin)
                        }
                        const token = document.cookie
                          .split("; ")
                          .find((r) => r.startsWith("token="))
                          ?.split("=")[1]
                        if (token && user) {
                          try {
                            await fetch("/api/graphql", {
                              method: "POST",
                              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                              body: JSON.stringify({
                                query: `
                                  mutation UpdateUserProfile($name: String!, $lockscreenPin: String) {
                                    updateUserProfile(name: $name, lockscreenPin: $lockscreenPin) {
                                      id
                                      lockscreenPin
                                    }
                                  }
                                `,
                                variables: { name: user.name || profileName, lockscreenPin }
                              })
                            })
                          } catch (e) { }
                        }
                        toast.success("PIN de acesso rápido salvo com sucesso!")
                      }}
                      className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-9 px-4 cursor-pointer"
                    >
                      Salvar PIN
                    </Button>
                    {lockscreenPin && (
                      <Button
                        type="button"
                        onClick={async () => {
                          setLockscreenPin("")
                          if (user) {
                            localStorage.removeItem(`lockscreenPin:${user.id}`)
                          }
                          const token = document.cookie
                            .split("; ")
                            .find((r) => r.startsWith("token="))
                            ?.split("=")[1]
                          if (token && user) {
                            try {
                              await fetch("/api/graphql", {
                                method: "POST",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({
                                  query: `
                                    mutation UpdateUserProfile($name: String!, $lockscreenPin: String) {
                                      updateUserProfile(name: $name, lockscreenPin: $lockscreenPin) {
                                        id
                                        lockscreenPin
                                      }
                                    }
                                  `,
                                  variables: { name: user.name || profileName, lockscreenPin: "" }
                                })
                              })
                            } catch (e) { }
                          }
                          toast.info("PIN removido.")
                        }}
                        variant="outline"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 text-xs font-semibold h-9 px-3 cursor-pointer"
                      >
                        Limpar PIN
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            <div className="h-20" />
          </TabsContent>
        </div>
      )}
    </Tabs>
  )
}
